import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Sun, TreePalm, Sailboat } from "lucide-react";
import MaskedReveal from "./MaskedReveal";
import usePrefersReducedMotion from "./usePrefersReducedMotion";

export const Hero = () => {
  const ref = useRef(null);
  const reduced = usePrefersReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const rawY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const rawScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.18]);
  const imgY = reduced ? 0 : rawY;
  const imgScale = reduced ? 1 : rawScale;

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Full-bleed resort photo */}
      <motion.img
        src="/resort-hero.webp"
        alt="Hotel Kohinoor resort at dusk — colonial buildings beside a reflecting pool"
        data-testid="hero-photo"
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 h-full w-full object-cover object-center"
        draggable="false"
      />
      {/* Warm tropical overlays for legibility */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#06121b]/85 via-[#06121b]/35 to-[#06121b]/45" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a2f47]/45 to-transparent" />

      {/* Content anchored low, above the wave */}
      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-6xl flex-col justify-end px-6 pt-32 pb-36 sm:pb-44">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="mb-7 flex items-center gap-3"
        >
          <Sun className="text-[#f5b301]" size={20} />
          <TreePalm className="text-[#5fd08a]" size={20} />
          <Sailboat className="text-[#ff8a8f]" size={20} />
          <span className="font-display text-xs sm:text-sm tracking-[0.32em] uppercase text-white/90">
            Hotel Kohinoor · Diu, India
          </span>
        </motion.div>

        <MaskedReveal
          as="h1"
          testid="hero-headline"
          lines={["Something", "beautiful is", "being rebuilt."]}
          delay={0.25}
          className="font-display text-[15vw] leading-[0.9] sm:text-7xl lg:text-8xl text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.35)]"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-[640px]"
        >
          <p
            className="text-lg sm:text-xl leading-relaxed text-white/90"
            data-testid="hero-message"
          >
            Welcome to <span className="font-semibold text-white">Hotel Kohinoor</span>.
            Our official website is currently under renewal — being rebuilt
            entirely in-house, thoughtfully and from the ground up. Thank you for
            your patience while we craft something worthy of your stay.
          </p>
          <a
            href="#verified-contact"
            data-testid="hero-scroll-cta"
            className="group mt-9 inline-flex items-center gap-3 rounded-full bg-[#12729c] px-6 py-3 font-display text-sm tracking-widest uppercase text-white shadow-[0_10px_30px_-8px_rgba(41,171,226,0.8)] transition-all duration-300 hover:bg-[#0f6288]"
          >
            Verified Contact
            <span className="h-px w-6 bg-white transition-all duration-300 group-hover:w-10" />
          </a>
        </motion.div>
      </div>

      {/* Layered tropical wave divider — echoes the logo's water */}
      <div className="absolute inset-x-0 bottom-0 z-10 leading-[0]" aria-hidden="true">
        <svg
          viewBox="0 0 1440 150"
          preserveAspectRatio="none"
          className="block h-[70px] w-full sm:h-[120px]"
        >
          <path
            fill="#2e4a9e"
            opacity="0.55"
            d="M0,70 C240,140 480,20 720,52 C960,84 1200,150 1440,86 L1440,150 L0,150 Z"
          />
          <path
            fill="#29abe2"
            opacity="0.9"
            d="M0,92 C240,150 480,44 720,76 C960,108 1200,158 1440,104 L1440,150 L0,150 Z"
          />
          <path
            fill="#fdfbf6"
            d="M0,116 C240,158 480,74 720,102 C960,130 1200,164 1440,122 L1440,150 L0,150 Z"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
